import { useEffect, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createIcons, icons } from 'lucide';
import './../App.css';
import './../AQWpackages.css';
import SEO from '../SEO';

const LATEST = {
  version: 'v1.17.0 - v1.18.0',
  date: '25 Aug 2026',
  title: 'Breadcrumbs, Subscriptions, New Simulators & QA Performance',
  summary: 'Platform-wide navigation and reliability release adding breadcrumbs, responsive navigation bars, a subscription service for reports and a mail service, alongside cleanup and speed optimizations. Production gains KPIs improvements and a weekly mortality permille index. Water Quality expands AQW Premium with an Oxygen/CO2 mass balance, introduces five experimental AQW Simula simulators, and adds biofilter capability tracking. Fish Health speeds up and improves Manual Measurements views, and QA Performance adds cross-batch mortality, growth, feeding and biofilter efficiency comparisons.',
  sections: [
    {
      label: 'General Integrity App',
      icon: 'layout-dashboard',
      notes: [
        'Breadcrumbs added (Water Quality, Fish Health, Production)',
        'Navigation bars: stretching correctly for different devices',
        'Cleaning: old paths, cards and URLs',
        'Optimizing: speed of requests and loading views',
        'Subscription service: users can subscribe to reports',
        'Deployment processes: optimized',
        'Mail service and capabilities',
      ],
    },
    {
      label: 'Production',
      icon: 'layers',
      notes: [
        'Batch Performance — KPIs_II: improvements',
        'Batch Performance — Mortality: weekly mortality permille index added',
      ],
    },
    {
      label: 'Water Quality',
      icon: 'droplet',
      notes: [
        'AQW Premium (Analysis) — Mass Balance: oxygen consumption',
        'AQW Premium (Models): Nephrocalcinosis Risk, ',
        'AQW Simula (Simulators) — Lye Dosing ',
        'AQW Simula (Simulators) — Degassing ',
        'AQW Simula (Simulators) — RAS Scenarios ',
        'AQW Simula (Simulators) — Batch Flow ',
        'AQW Simula (Simulators) — Fish Crowding ',
        'Biofilter: capabilities and efficiency',
      ],
    },
    {
      label: 'Fish Health',
      icon: 'heart-pulse',
      notes: [
        'Manual Measurements — improved refreshing times',
        'Manual Measurements — improved views',
      ],
    },
    {
      label: 'Quality Assessment (QA)',
      icon: 'shield-check',
      notes: [
        'Performance — Mortality comparison: all batches accumulated mortality',
        'Performance — Growth comparison: all batches growth trends',
        'Performance — Feeding targets: all batches feeding behavior',
        'Performance — Biofilter efficiency: all batches Ammonium values and biofilm coping capabilities',
      ],
    },
    {
      label: 'AquaWARE DB',
      icon: 'database',
      notes: [
        'Optimizations',
        'Alkalinity properly mapped (alk_sat vs alkalinity_mgl)',
        'Additional fields for Batch Management',
        'Additional choices for tags — Diary',
      ],
    },
  ],
};

const PREVIOUS = [
  {
    version: 'v1.12.0',
    date: '02 Jul 2026',
    type: 'major',
    title: 'Plot Sync, Export, Custom Panels & AQW AI Assistant',
    sections: [
      {
        label: 'Platform',
        notes: [
          'Plot synchronization: all plots share navigation coordinates and zoom together',
          'Progress bar for multi-plot loading pages (e.g. Water Quality)',
          'Export to PDF, PNG (zip) or interactive HTML added to Custom, KPIs and Performance (Performance in test mode)',
          'Light/Dark mode fully functional, light mode fixed and reviewed',
        ],
      },
      {
        label: 'Dashboard',
        notes: [
          'Dynamic information (work in progress)',
        ],
      },
      {
        label: 'Production',
        notes: [
          'General: main page selector changed; Batch Details (CVreport) and Batch Management are now separate views',
          'Batch Management: improved biomass entry pages, batch ID shown, default status filters',
          'Batch Detail: fixed slow loading issue',
          'Batch Detail: KPIs view added',
        ],
      },
      {
        label: 'Water Quality',
        notes: [
          'Custom section added: build and export your own panel of plots',
        ],
      },
      {
        label: 'Fish Health',
        notes: [
          'Manual Measurements added',
          'Improved visualization of previous Manual Measurements, Mortality and Growth',
          'Dangerous values highlighted in Manual Measurements',
          'Manual Measurements pagination performance improved',
        ],
      },
      {
        label: 'AQW AI',
        notes: [
          'New structure: Assistant, Smart Alarms and Autopilot',
          'Assistant section added',
        ],
      },
      {
        label: 'QA',
        notes: [
          'New structure: Certifications, Discharge/Environment and Performance',
          'Performance section (WIP): batch and inter-tank comparisons across the site',
        ],
      },
      {
        label: 'Other',
        notes: [
          'Optimization rules, feature profiling, deployment activities, typo fixes and cosmetic consistency across sections',
        ],
      },
    ],
  },
  {
    version: 'v1.11.0',
    date: '12 May 2026',
    type: 'minor',
    title: 'Fish Health CRUD, Timestamps & Production Archive',
    sections: [
      {
        label: 'Fish Health',
        notes: [
          'Growth Reports: business logic relation to mortality forms',
          'Growth Reports: Delete and Edit actions',
          'Event Reports: Delete and Edit actions',
          'Timestamps changed from UTC to local time',
          'Visual Guide / Glossary for death causes in mortality form',
        ],
      },
      {
        label: 'Production',
        notes: [
          'CVreports now available for Archived Batches',
          'Additions in Batch Management',
        ],
      },
    ],
  },
  {
    version: 'v1.10.0',
    date: '17 Apr 2026',
    type: 'major',
    title: 'Plots Homogenisation, CVreport & Dark Mode',
    sections: [
      {
        label: 'Platform',
        notes: [
          'Plots homogenisation — positions, grids, lines, legend and mod-bar',
          'Dark mode set as default (light mode optimisation ongoing)',
          'Optimisation to increase speed and responsiveness',
        ],
      },
      {
        label: 'Water Quality',
        notes: [
          'NH₃-calculated plot added to CVreport and Models',
          'Ammonium raw plots — manual measurements',
        ],
      },
      {
        label: 'Production',
        notes: [
          'Combining batch data across plants in CVreport',
          'FCR line added to CVreport — biomass section',
          'Headlines info automatically fetched from DB in CVreport',
        ],
      },
    ],
  },
  {
    version: 'v1.8.0',
    date: '19 Mar 2026',
    type: 'minor',
    title: 'Water Quality Expansion, Fish Health Edits & DevOps',
    sections: [
      {
        label: 'DevOps',
        notes: [
          'Updated Docker image to image with tag',
          'Configurable links for Landax, Maintenance and ORM GRASP',
        ],
      },
      {
        label: 'Water Quality — Degas',
        notes: [
          'New plot: Feed vs CO₂ [g]',
          'Degassing efficiency reviewed (not definitive)',
          'Total CO₂ emissions plot',
          'Skimming and variance plots',
          'AQD pressure plots',
        ],
      },
      {
        label: 'Water Quality — Dashboard',
        notes: ['Redox plot', 'Turbidity and fargetall plots'],
      },
      {
        label: 'Water Quality — Raw Values',
        notes: ['Oxygen supply plot added'],
      },
      {
        label: 'Water Quality — Models',
        notes: ['TAN model correction in volumes used'],
      },
      {
        label: 'Fish Health',
        notes: [
          'Edit existing mortality entries through the add-entries UI',
          'Edit and delete mortality buttons in prev_specific',
          'Mortality list filtering by batch and site',
          'Mortality ordering by timestamp',
          'Dropdown limited to 10 items at a time with filtering',
          'Mortality plots rearranged in Production (CVreport)',
        ],
      },
    ],
  },
  {
    version: 'v1.5.0',
    date: '18 Feb 2026',
    type: 'minor',
    title: 'Database Structure, Forecast & Code Optimisation',
    sections: [
      {
        label: 'Platform',
        notes: [
          'New database structure for manual values (from Baring Excel files)',
          'New nomenclature in the database',
          'Automating skimming visualisations and stress level parameters',
          'Communications layer for new cloud items',
          'Forecast functionality — under test',
          'Fish Health: new fields for manual data entry',
          'Code cleaning and optimisation',
        ],
      },
    ],
  },
  {
    version: 'v1.2.0',
    date: '19 Dec 2025',
    type: 'patch',
    title: 'Fish Health DB Fields, Lye & Feed Curves, Local Time',
    sections: [
      {
        label: 'AquaWARE-cloud',
        notes: ['New fields added to tables for Fish Health section'],
      },
      {
        label: 'AquaWARE-integrity',
        notes: [
          'Accommodation of new DB fields in integrity',
          'Lye curve added in Water Quality — Dashboard',
          'Feed curve added in Water Quality — Dashboard',
          'Changed from UTC to local time in all plots',
        ],
      },
    ],
    upcoming: [
      'Fish Health section fully up and running',
      'Water Quality — Forecast: CO₂, pH, feed',
      'iRAS — lye suggestions',
      'Production — CVreport dual tank',
    ],
  },
  {
    version: 'v1.1.0',
    date: '12 Dec 2025',
    type: 'minor',
    title: 'Initial Platform Release — Feature Overview',
    sections: [
      {
        label: 'Water Quality',
        notes: [
          'Live data for RAS1_22m and RAS2_30m tanks',
          'AquaPREMIUM: Dashboard, Raw Values, Models (TIC, TDS, TAN) and Degass',
          'Default view: last week — configurable time span and interval',
        ],
      },
      {
        label: 'Production',
        notes: [
          'Biomass Control: batch list with CVreport (under final construction)',
          'Pie charts showing optimal / good / low risk / high risk day proportions',
        ],
      },
      {
        label: 'Simulators',
        notes: ['Growth simulators live — remaining simulators coming soon'],
      },
      {
        label: 'Notes',
        notes: [
          'Dark mode recommended — select via upper-right corner (light theme not fully optimised)',
          'Browser will remember your theme choice after first selection',
        ],
      },
    ],
    upcoming: [
      'Water Quality — Forecast CO₂',
      'AI (Lye)',
      'Fish Health data entries',
      'Degassing: updated model',
    ],
    roadmap: [
      'CVreport for batches spanning two or more tanks',
      'Additional plots from Fish Health module (turbidity, mortality)',
      'Descriptive statistics in plots and CVreport',
      'Remaining simulators on web (energy, alkalinity, enthalpy…)',
      'Inter-tank comparison',
      'SCADA connection for lye and feed suggestions',
      'SCADA integration — view only',
    ],
  },
];

const TYPE_CONFIG: Record<string, { color: string; bg: string; border: string; label: string }> = {
  major: { color: '#FFAD60', bg: 'rgba(255,173,96,0.15)', border: 'rgba(255,173,96,0.3)', label: 'Major' },
  minor: { color: '#9ED3CD', bg: 'rgba(158,211,205,0.15)', border: 'rgba(158,211,205,0.3)', label: 'Minor' },
  patch: { color: '#a259ff', bg: 'rgba(162,89,255,0.15)', border: 'rgba(162,89,255,0.3)', label: 'Patch' },
};

const Releases = () => {
  const navigate = useNavigate();

  useLayoutEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  useEffect(() => {
    createIcons({ icons });
  }, []);

  return (
    <div style={{ paddingTop: 64 }}>
      <SEO
        title="Release Notes"
        description="Latest AquaWARE platform updates, new features and changelogs — see what's new in water quality, production, fish health, QA and the AquaWARE DB."
        path="/releases"
      />

      {/* Banner */}
      <div style={{
        position: 'relative', width: '100%', overflow: 'hidden',
        backgroundImage: 'url(/images/banners_AQW_page_releases.webp)',
        backgroundSize: 'cover', backgroundPosition: 'center',
        minHeight: 360,
        display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
      }}>
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'linear-gradient(to top, rgba(10,10,12,0.75) 0%, rgba(10,10,12,0.35) 50%, rgba(10,10,12,0.15) 100%)',
        }} />
        <button
          className="back-home-btn"
          onClick={() => navigate('/')}
          style={{
            position: 'absolute', top: 20, left: 50, zIndex: 2,
            background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.25)',
            cursor: 'pointer', alignItems: 'center', gap: 8,
            color: '#fff', fontSize: 13, padding: '6px 14px', borderRadius: 8,
          }}
        >
          <i data-lucide="arrow-left" style={{ width: 16, height: 16 }} /> Back to Home
        </button>
        <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <p style={{
            fontSize: 11, fontWeight: 700, letterSpacing: '0.18em',
            textTransform: 'uppercase', color: '#9ED3CD',
            margin: '0 0 12px', fontFamily: "'JetBrains Mono', monospace",
          }}>// changelog</p>
          <h1 style={{ margin: '0 0 14px' }}>News & Releases</h1>
          <p style={{ color: 'var(--muted)', fontSize: 15, margin: 0 }}>
            Latest updates, new features, and patch notes for AquaWARE
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '56px 24px' }}>

        {/* Latest release */}
        <div style={{ marginBottom: 72 }}>
          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#FFAD60', margin: '0 0 20px', fontFamily: "'JetBrains Mono', monospace" }}>// latest release</p>
          <div className="plan-card featured" style={{ padding: 32 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 22, fontWeight: 700, color: '#9ED3CD', fontFamily: "'JetBrains Mono', monospace" }}>{LATEST.version}</span>
              <span style={{ fontSize: 13, color: 'var(--muted)', marginLeft: 'auto' }}>{LATEST.date}</span>
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 500, margin: '0 0 12px', color: 'var(--text)' }}>{LATEST.title}</h2>
            <p style={{ color: 'var(--muted)', fontSize: 14, margin: '0 0 28px', lineHeight: 1.7 }}>{LATEST.summary}</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '20px 28px', textAlign: 'left' }}>
              {LATEST.sections.map((sec, si) => (
                <div key={si}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 8px' }}>
                    <i data-lucide={sec.icon} style={{ width: 14, height: 14, color: '#9ED3CD' }} />
                    <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#9ED3CD', margin: 0 }}>{sec.label}</p>
                  </div>
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {sec.notes.map((note, i) => (
                      <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: 'var(--text)', lineHeight: 1.5 }}>
                        <i data-lucide="chevron-right" style={{ width: 14, height: 14, color: '#9ED3CD', flexShrink: 0, marginTop: 2 }} />
                        {note}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Previous releases — timeline */}
        <div>
          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--muted)', margin: '0 0 32px', fontFamily: "'JetBrains Mono', monospace" }}>// previous releases</p>
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', left: 16, top: 0, bottom: 0, width: 1, background: 'rgba(255,255,255,0.07)' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
              {PREVIOUS.map((rel) => {
                const cfg = TYPE_CONFIG[rel.type];
                return (
                  <div key={rel.version} style={{ display: 'flex', gap: 28 }}>
                    {/* Dot */}
                    <div style={{ width: 33, flexShrink: 0, display: 'flex', justifyContent: 'center', paddingTop: 20 }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: cfg.color, boxShadow: `0 0 8px ${cfg.color}`, flexShrink: 0 }} />
                    </div>
                    {/* Card */}
                    <div className="plan-card" style={{ padding: '20px 24px', flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 15, fontWeight: 700, color: cfg.color, fontFamily: "'JetBrains Mono', monospace" }}>{rel.version}</span>
                        <span style={{ fontSize: 12, color: 'var(--muted)', marginLeft: 'auto' }}>{rel.date}</span>
                      </div>
                      <h3 style={{ fontSize: 15, fontWeight: 500, margin: '0 0 16px', color: 'var(--text)', paddingTop: 0 }}>{rel.title}</h3>

                      {/* Sections */}
                      {rel.sections.map((sec, si) => (
                        <div key={si} style={{ marginBottom: 14 }}>
                          {rel.sections.length > 1 && (
                            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: cfg.color, margin: '0 0 6px' }}>{sec.label}</p>
                          )}
                          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 5 }}>
                            {sec.notes.map((note, i) => (
                              <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: 'var(--muted)' }}>
                                <i data-lucide="chevron-right" style={{ width: 14, height: 14, color: cfg.color, flexShrink: 0, marginTop: 2 }} />
                                {note}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}

                      {/* Upcoming */}
                      {'upcoming' in rel && rel.upcoming && (
                        <div style={{ marginTop: 16, padding: '12px 16px', background: 'rgba(158,211,205,0.05)', border: '1px solid rgba(158,211,205,0.12)', borderRadius: 8 }}>
                          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#9ED3CD', margin: '0 0 8px' }}>Imminent updates</p>
                          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {rel.upcoming.map((item, i) => (
                              <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: 'var(--muted)' }}>
                                <i data-lucide="clock" style={{ width: 12, height: 12, color: '#9ED3CD', flexShrink: 0, marginTop: 2 }} />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Roadmap */}
                      {'roadmap' in rel && rel.roadmap && (
                        <div style={{ marginTop: 10, padding: '12px 16px', background: 'rgba(255,173,96,0.05)', border: '1px solid rgba(255,173,96,0.12)', borderRadius: 8 }}>
                          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#FFAD60', margin: '0 0 8px' }}>Coming in the following months</p>
                          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {rel.roadmap.map((item, i) => (
                              <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: 'var(--muted)' }}>
                                <i data-lucide="map" style={{ width: 12, height: 12, color: '#FFAD60', flexShrink: 0, marginTop: 2 }} />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* End image */}
        <div style={{ marginTop: 72, borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)', maxWidth: 600, margin: '72px auto 0' }}>
          <img src="/images/patchday.webp" alt="Patch day" style={{ width: '100%', height: 'auto', display: 'block', filter: 'brightness(0.65) saturate(0.7) opacity(0.85)' }} />
        </div>

      </div>
    </div>
  );
};

export default Releases;
